/**
 * DialogoDeMotivo — rechazar o anular una solicitud pidiendo el motivo.
 *
 * El motivo es obligatorio porque le llega por correo al trabajador (#1490):
 * sin él se entera del rechazo o la anulación sin saber por qué. Es el mismo
 * criterio que el diálogo de «Mi equipo» en el portal (`SolicitudesPorResolver`).
 *
 * ⚠️ El clic afuera NO cierra (`dismissOnScrimClick={false}`): a diferencia de
 * una confirmación, acá hay texto escrito que se perdería.
 */
import { useState } from 'react'
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/ds/Button'
import { extractErrorMessage } from '@/lib/errorUtils'

export interface DialogoDeMotivoProps {
  titulo: string
  subtitulo?: string
  /** Texto bajo el campo: qué pasa al confirmar. */
  ayuda: string
  confirmar: string
  pendiente?: boolean
  onConfirmar: (motivo: string, alFallar: (e: unknown) => void) => void
  onCerrar: () => void
}

export function DialogoDeMotivo({
  titulo, subtitulo, ayuda, confirmar, pendiente = false, onConfirmar, onCerrar,
}: DialogoDeMotivoProps) {
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const enviar = () => {
    if (!motivo.trim()) { setError('Falta el motivo.'); return }
    setError(null)
    onConfirmar(motivo.trim(), e => setError(extractErrorMessage(e, 'No se pudo completar la acción.')))
  }

  return (
    <Modal
      open onClose={onCerrar} size="sm" dismissOnScrimClick={false}
      title={titulo} subtitle={subtitulo}
      actions={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          <Button variant="danger-solid" loading={pendiente} onClick={enviar}>{confirmar}</Button>
        </div>
      }
    >
      <div className="space-y-2">
        <label htmlFor="dialogo-motivo" className="block text-xs font-medium text-content-muted">
          Motivo *
        </label>
        <textarea
          id="dialogo-motivo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
          className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content resize-none"
        />
        <p className="text-xs text-content-subtle">{ayuda}</p>
        {error && <p role="alert" className="text-sm text-danger-solid">{error}</p>}
      </div>
    </Modal>
  )
}
